import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import SocialLoginFragment from '@app/site/authentication/fragment/SocialRegistrationFragment';
import useSiteState from '@app/state/SiteState';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import FormInputText from '@app/component/form/input/text/FormInputText';
import FormCheckbox from '@app/component/form/input/checkbox/FormCheckbox';
import FormButton from '@app/component/form/button/FormButton';
import SecurityService from '@app/model/_security/service/SecurityService';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from '@app/component/translation/Translation';


const SignInPage = React.memo(() => {

	const form = useForm();
	const siteState = useSiteState();
	const toaster = useToaster();
	const [isPending, setIsPending] = useState<boolean>();
	const navigate = useNavigate();
	const { t } = useTranslation();

	const signInService = useMutation({
		mutationFn: SecurityService.signIn,
		onSuccess: (principalWithToken) => {
			SecurityService.login(principalWithToken);
			navigate('/oauth2/redirect');
			setIsPending(false);
		},
		onError: (error: string) => {
			toaster.error('Sign in to your account', t(error));
			setIsPending(false);
		},
		onMutate: () => {
			siteState.logout();
			SecurityService.removeToken();
			setIsPending(true);
		},
	});

	const onSubmit = (data: { username: string, password: string }) => {
		signInService.mutate(data);
	};

	return <React.Fragment>
		<div className="min-h-full bg-white">
			<div className="flex lg:h-dvh min-w-max content-center justify-center flex-1">
				<div className="flex flex-1 flex-col justify-center px-6 py-12">
					<div className="mx-auto w-full max-w-sm lg:w-96">
						<div>
							<a href="http://roompulse.io">
								<img
								className="h-10 w-auto"
								src="/assets/img/brand/logo-icon.png"
								alt="RoomPulse"/>
							</a>
							<h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-gray-900">
								Sign in to your account
							</h2>
							<p className="mt-2 text-sm leading-6 text-gray-500">
								Don't have an account yet?{' '}
								<Link to="/sign-up" className="font-semibold text-blue-600 hover:text-blue-500"
											title="Click to create a new account"> Create a new account</Link>
							</p>
						</div>

						<div className="mt-10">
							<div>
								<form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
									<FormInputText name="username" form={form} label="Email address" isRequired />
									<FormInputText name="password" type="password" form={form} label="Password"
																 isRequired />

									<div className="flex items-center justify-between">
										<FormCheckbox name="rememberMe" form={form} label="Remember me" />
										<div className="text-sm">
											<Link to="/forgot-password"
														className="font-medium text-blue-600 hover:text-blue-500">
												Forgot your password?
											</Link>
										</div>
									</div>

									<div>
										<FormButton state="primary" type="submit" extraClassName="w-full"
																isLoading={isPending}>
											Sign in
										</FormButton>
									</div>
								</form>
							</div>

							<div className="mt-10">
								<SocialLoginFragment onPending={setIsPending} />
							</div>
						</div>
					</div>
				</div>
				<div className="relative hidden flex-1 lg:block">
					<img
						className="absolute inset-0 h-full w-full object-cover"
						src="https://images.unsplash.com/photo-1567016376408-0226e4d0c1ea?q=80&w=2574&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
						alt=""
					/>
				</div>
			</div>
		</div>
	</React.Fragment>;
});

export default SignInPage;